import { useState } from 'react';

const EMPTY_FORM = {
    name: '',
    description: '',
    buyer_discount_percent: 0,
    level_percents_text: '1:3, 2:1.5, 3:0.75',
    max_depth: 3,
    max_total_percent: 6,
    min_margin_percent: 3,
    requires_active: false,
    active_days_window: 30,
    min_purchase_count: 1,
    is_active: false,
};

const parseLevelPercents = (text) => {
    const result = {};
    const parts = String(text || '').split(',');
    for (const part of parts) {
        const [level, percent] = part.split(':').map((s) => s.trim());
        if (!level || !percent) continue;
        const levelNum = parseInt(level, 10);
        const percentNum = parseFloat(percent);
        if (Number.isNaN(levelNum) || Number.isNaN(percentNum)) continue;
        result[String(levelNum)] = percentNum;
    }
    return result;
};

const formatLevelPercents = (obj) => {
    if (!obj) return '';
    return Object.entries(obj)
        .sort(([a], [b]) => Number(a) - Number(b))
        .map(([level, percent]) => `${level}:${percent}`)
        .join(', ');
};

export default function ConfigFormModal({ mode, config, onClose, onSubmit, loading }) {
    const isEdit = mode === 'edit';

    const [form, setForm] = useState(() => {
        if (isEdit && config) {
            return {
                ...EMPTY_FORM,
                ...config,
                level_percents_text: formatLevelPercents(config.level_percents),
            };
        }
        return EMPTY_FORM;
    });
    const [error, setError] = useState('');

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!form.name.trim()) {
            setError('نام برنامه الزامی است.');
            return;
        }

        const levelPercents = parseLevelPercents(form.level_percents_text);
        if (Object.keys(levelPercents).length === 0) {
            setError('حداقل یک سطح باید تعریف شود. مثال: 1:3, 2:1.5');
            return;
        }

        const payload = {
            name: form.name.trim(),
            description: form.description || null,
            buyer_discount_percent: Number(form.buyer_discount_percent) || 0,
            level_percents: levelPercents,
            max_depth: Number(form.max_depth) || 1,
            max_total_percent: Number(form.max_total_percent) || 0,
            min_margin_percent: Number(form.min_margin_percent) || 0,
            requires_active: !!form.requires_active,
            active_days_window: Number(form.active_days_window) || 30,
            min_purchase_count: Number(form.min_purchase_count) || 0,
            is_active: !!form.is_active,
        };

        try {
            await onSubmit(payload);
        } catch (err) {
            setError(err.message);
        }
    };

    return (
        <div className="admin-modal-backdrop" onClick={onClose}>
            <div
                className="admin-modal admin-modal-lg"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="admin-modal-header">
                    <h3 className="admin-modal-title">
                        {isEdit ? 'ویرایش برنامه پورسانت' : 'ایجاد برنامه پورسانت'}
                    </h3>
                    <button className="admin-modal-close" onClick={onClose}>✕</button>
                </div>

                <form onSubmit={handleSubmit} className="admin-modal-body">
                    {error && <div className="error-banner">{error}</div>}

                    <div className="form-grid">
                        <label>
                            <span>نام برنامه *</span>
                            <input
                                name="name"
                                value={form.name}
                                onChange={handleChange}
                                placeholder="مثلاً: برنامه عادی"
                                required
                            />
                        </label>

                        <label>
                            <span>توضیحات</span>
                            <input
                                name="description"
                                value={form.description || ''}
                                onChange={handleChange}
                                placeholder="توضیح کوتاه"
                            />
                        </label>
                    </div>

                    <div className="form-grid">
                        <label>
                            <span>درصد تخفیف خریدار جدید</span>
                            <input
                                type="number"
                                name="buyer_discount_percent"
                                value={form.buyer_discount_percent}
                                onChange={handleChange}
                                min="0"
                                max="100"
                                step="0.1"
                            />
                        </label>

                        <label>
                            <span>حداکثر عمق زنجیره</span>
                            <input
                                type="number"
                                name="max_depth"
                                value={form.max_depth}
                                onChange={handleChange}
                                min="1"
                                max="20"
                            />
                        </label>
                    </div>

                    <label className="full-width">
                        <span>درصد هر سطح (قالب: level:percent، با کاما جدا کنید)</span>
                        <input
                            name="level_percents_text"
                            value={form.level_percents_text}
                            onChange={handleChange}
                            placeholder="1:3, 2:1.5, 3:0.75"
                            required
                        />
                        <small>مثال: 1:3 یعنی سطح ۱ معادل ۳٪</small>
                    </label>

                    <div className="form-grid">
                        <label>
                            <span>سقف مجموع پورسانت (٪ از فروش)</span>
                            <input
                                type="number"
                                name="max_total_percent"
                                value={form.max_total_percent}
                                onChange={handleChange}
                                min="0"
                                max="100"
                                step="0.1"
                            />
                        </label>

                        <label>
                            <span>حداقل حاشیه سود (٪)</span>
                            <input
                                type="number"
                                name="min_margin_percent"
                                value={form.min_margin_percent}
                                onChange={handleChange}
                                min="0"
                                max="100"
                                step="0.1"
                            />
                        </label>
                    </div>

                    <div className="form-grid">
                        <label className="checkbox-label">
                            <input
                                type="checkbox"
                                name="requires_active"
                                checked={form.requires_active}
                                onChange={handleChange}
                            />
                            <span>شرط فعالیت کاربر (خرید در بازه اخیر)</span>
                        </label>

                        <label className="checkbox-label">
                            <input
                                type="checkbox"
                                name="is_active"
                                checked={form.is_active}
                                onChange={handleChange}
                            />
                            <span>این برنامه فعال باشد</span>
                        </label>
                    </div>

                    {form.requires_active && (
                        <div className="form-grid">
                            <label>
                                <span>بازه فعالیت (روز)</span>
                                <input
                                    type="number"
                                    name="active_days_window"
                                    value={form.active_days_window}
                                    onChange={handleChange}
                                    min="1"
                                />
                            </label>

                            <label>
                                <span>حداقل تعداد خرید در بازه</span>
                                <input
                                    type="number"
                                    name="min_purchase_count"
                                    value={form.min_purchase_count}
                                    onChange={handleChange}
                                    min="0"
                                />
                            </label>
                        </div>
                    )}

                    <div className="admin-modal-footer">
                        <button
                            type="button"
                            onClick={onClose}
                            className="btn btn-ghost"
                        >
                            انصراف
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="btn btn-primary"
                        >
                            {loading ? 'در حال ذخیره...' : isEdit ? 'ذخیره تغییرات' : 'ایجاد'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}